import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ADVISOR_ACCEPT, ADVISOR_MAX_FILES, ADVISOR_MAX_TOTAL_MB } from '@entities/progress';
import { formatFileCount, totalSize } from '@shared/lib/formatFile';
import { IconArrowUp, IconFile, IconGlobe, IconPaperclip, IconX } from '@shared/ui/icons';

/* Поле вопроса Эксперту в духе ChatGPT: крупная скруглённая плашка, текст
   сверху, под ним панель — скрепка, отметка «поиск в интернете», отправка.
   Enter — отправить, Shift+Enter — новая строка; файлы — скрепкой,
   перетаскиванием и вставкой скриншота из буфера. Черновик и файлы живут у
   родителя — при ошибке ответа не теряются. */

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const Wrap = styled.form<{ $drag: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2.5)}`};
  border-radius: 28px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    box-shadow ${({ theme }) => theme.motion.fast};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
  ${({ $drag, theme }) =>
    $drag &&
    css`
      border-style: dashed;
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primarySoft};
    `}
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
    `}
`;

/* До 50 файлов: больше трёх рядов — прокрутка, поле не уезжает за экран. */
const Files = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  max-height: 172px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${({ theme }) => `0 ${theme.spacing(1)}`};
`;

const FileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  max-width: 280px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(1.5)} ${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;

  img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.radius.sm};
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  small {
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }
`;

const Text = styled.textarea`
  width: 100%;
  min-height: 28px;
  max-height: 40vh;
  resize: none;
  border: 0;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 16px;
  line-height: 1.55;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  /* Фокус показывает тень всего поля (Wrap:focus-within), не сам textarea. */
  &:focus-visible {
    box-shadow: none;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Spacer = styled.span`
  flex: 1;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const RoundBtn = styled.button<{ $primary?: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme, $primary }) => ($primary ? 'transparent' : theme.colors.border)};
  background: ${({ theme, $primary }) => ($primary ? theme.colors.primary : 'transparent')};
  color: ${({ theme, $primary }) => ($primary ? '#fff' : theme.colors.text.secondary)};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast},
    transform ${({ theme }) => theme.motion.fast};

  &:hover:not(:disabled) {
    background: ${({ theme, $primary }) => ($primary ? theme.colors.primaryHover : theme.colors.surfaceHover)};
    color: ${({ theme, $primary }) => ($primary ? '#fff' : theme.colors.text.primary)};
  }
  &:active:not(:disabled) {
    transform: scale(0.94);
  }
  &:disabled {
    cursor: not-allowed;
  }
  /* Отправлять нечего — кнопка серая, как в ChatGPT; пока Эксперт отвечает,
     кнопка с крутилкой остаётся яркой. */
  &:disabled:not([aria-busy='true']) {
    ${({ theme, $primary }) =>
      $primary
        ? css`
            background: ${theme.colors.surfaceHover};
            color: ${theme.colors.text.muted};
          `
        : css`
            opacity: 0.4;
          `}
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

const WebChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;

  @media (max-width: 420px) {
    span {
      display: none;
    }
  }
`;

const Counter = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-variant-numeric: tabular-nums;
`;

const FilesInfo = styled(Counter)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipX = styled.button`
  display: inline-flex;
  border: 0;
  padding: 2px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Foot = styled.div`
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  font-size: 12px;
  line-height: 1.45;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};

  /* На телефоне клавиатурных подсказок не нужно. */
  @media (max-width: 560px) {
    .keys {
      display: none;
    }
  }
`;

function fmtSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`;
}

function isPreviewable(f: File): boolean {
  return /^image\/(jpeg|png|webp|gif)$/.test(f.type);
}

function Preview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url ? <img src={url} alt="" /> : null;
}

export interface ComposerProps {
  text: string;
  files: File[];
  busy: boolean;
  disabled: boolean;
  disabledHint?: string;
  /** Модель ищет в интернете — показать отметку в панели поля. */
  webSearch?: boolean;
  /** Меняется — поле забирает фокус с кареткой в конце (после подсказки). */
  focusSignal?: number;
  onText: (v: string) => void;
  onFiles: (files: File[]) => void;
  onError: (msg: string | null) => void;
  onSubmit: () => void;
}

export function Composer({
  text,
  files,
  busy,
  disabled,
  disabledHint,
  webSearch = false,
  focusSignal = 0,
  onText,
  onFiles,
  onError,
  onSubmit,
}: ComposerProps) {
  const area = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  // Высота по содержимому (до max-height).
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  useEffect(() => {
    const el = area.current;
    if (!el || busy || disabled) return;
    // Без прокрутки: иначе страница уезжает к полю и прячет шапку.
    el.focus({ preventScroll: true });
    if (focusSignal) el.setSelectionRange(el.value.length, el.value.length);
  }, [busy, disabled, focusSignal]);

  /* Добавляем по порядку, пока помещаются оба лимита вопроса (число файлов и
     их общий вес); что не поместилось — не добавляем и говорим почему. */
  function addFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    if (!incoming.length) return;
    const next = [...files];
    let total = totalSize(files);
    let overCount = 0;
    let overSize = 0;
    for (const f of incoming) {
      if (next.length >= ADVISOR_MAX_FILES) {
        overCount += 1;
      } else if (total + f.size > ADVISOR_MAX_TOTAL_MB * 1024 * 1024) {
        overSize += 1;
      } else {
        next.push(f);
        total += f.size;
      }
    }
    if (next.length > files.length) onFiles(next);
    const why: string[] = [];
    if (overSize) {
      why.push(
        `Все файлы вопроса вместе — не больше ${ADVISOR_MAX_TOTAL_MB} МБ, не добавлено: ${formatFileCount(overSize)}`,
      );
    }
    if (overCount) {
      why.push(
        `К одному вопросу — не больше ${ADVISOR_MAX_FILES} файлов, не добавлено: ${formatFileCount(overCount)}`,
      );
    }
    onError(why.length ? why.join('. ') : null);
  }

  const canSend = !busy && !disabled && (text.trim().length > 0 || files.length > 0);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const pasted = Array.from(e.clipboardData.files);
    if (pasted.length) {
      e.preventDefault();
      // Скриншот из буфера приходит как «image.png» — даём понятное имя.
      const stamp = new Date().toLocaleTimeString('ru-RU').replace(/:/g, '-');
      addFiles(
        pasted.map((f) =>
          f.name === 'image.png' ? new File([f], `Скриншот ${stamp}.png`, { type: f.type }) : f,
        ),
      );
    }
  }

  function onDrop(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    setDrag(false);
    if (!disabled && !busy) addFiles(e.dataTransfer.files);
  }

  return (
    <Box>
      <Wrap
        $drag={drag}
        $disabled={disabled}
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) onSubmit();
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDrag(true);
          }
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false);
        }}
        onDrop={onDrop}
      >
        {files.length > 0 && (
          <Files>
            {files.map((f, i) => (
              <FileChip key={`${f.name}-${i}`} title={f.name}>
                {isPreviewable(f) ? <Preview file={f} /> : <IconFile size={18} />}
                <span>{f.name}</span>
                <small>{fmtSize(f.size)}</small>
                <ChipX
                  type="button"
                  aria-label={`Убрать файл ${f.name}`}
                  disabled={busy}
                  onClick={() => onFiles(files.filter((_, j) => j !== i))}
                >
                  <IconX size={14} />
                </ChipX>
              </FileChip>
            ))}
          </Files>
        )}
        <Text
          ref={area}
          rows={1}
          value={text}
          disabled={disabled}
          placeholder={
            disabled
              ? (disabledHint ?? 'Эксперт недоступен')
              : 'Спросите Эксперта: письмо, разбор договора, расчёт, справка…'
          }
          onChange={(e) => onText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          aria-label="Вопрос Эксперту"
        />
        <Toolbar>
          <RoundBtn
            type="button"
            aria-label="Приложить файлы или фото"
            title={`Приложить файлы или фото — до ${ADVISOR_MAX_FILES} штук, все вместе до ${ADVISOR_MAX_TOTAL_MB} МБ; можно и перетащить в поле`}
            disabled={busy || disabled || files.length >= ADVISOR_MAX_FILES}
            onClick={() => picker.current?.click()}
          >
            <IconPaperclip size={18} />
          </RoundBtn>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            accept={ADVISOR_ACCEPT}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {webSearch && (
            <WebChip title="Эксперт сам ищет свежие сведения в интернете и даёт ссылки на источники">
              <IconGlobe size={15} />
              <span>Поиск в интернете</span>
            </WebChip>
          )}
          {files.length > 1 && (
            <FilesInfo
              title={`К одному вопросу — до ${ADVISOR_MAX_FILES} файлов, все вместе до ${ADVISOR_MAX_TOTAL_MB} МБ`}
            >
              {formatFileCount(files.length)} · {fmtSize(totalSize(files))} из {ADVISOR_MAX_TOTAL_MB} МБ
            </FilesInfo>
          )}
          <Spacer />
          <RoundBtn
            type="submit"
            $primary
            disabled={!canSend}
            aria-busy={busy}
            aria-label={busy ? 'Эксперт отвечает' : 'Отправить'}
          >
            {busy ? <Spinner aria-hidden /> : <IconArrowUp size={19} />}
          </RoundBtn>
        </Toolbar>
      </Wrap>
      <Foot>
        Эксперт может ошибаться — важные выводы проверяйте.
        <span className="keys"> Enter — отправить, Shift+Enter — новая строка.</span>
      </Foot>
    </Box>
  );
}
